import { action, makeObservable, observable, flow } from "mobx";
import { getAgentById, getDocumentsByAgent, getDrivesByAgent, updateAgent, assignAgentToDocument, createAgent, unassignAgentFromDocument } from "../../services";
import { assignAgentToVault } from "../../services/vault";
import AgentListStore from "./agentListStore";

class AgentStore {
    agentListStore = null;
    id = null;
    uuid = "";
    name = "";
    description = "";
    type = "";
    category = "";
    
    tenantId = "";
    instructionUuid = "";
    metadata = {};
    createdAt = "";
    owner = "";
    createdBy = "";
    updatedAt = "";
    status = "";
    version = null;
    scope = "";
    tags = "";
    vaultUuid = "";
    starters = [];
    

    isLoading = false;
    error = null;
    lastSaveTimeout = null;
    isSaving = false;
    isCreating = false;
    agentCreated = false;
    showSavingAlert = false;
    documents = [];
    drives = [];
    documentsLoading = false;
    drivesLoading = false;
    assignDocumentLoading = false;
    assignVaultLoading = false;
    errorCreatingAgent = false;
    lastLoadedAgentId = null;
    lastLoadedVaultId = null;

    lastSaveRequestId = 0;
    lastSaveSnapshot = null;

    constructor(agentListStore) {
        this.agentListStore = agentListStore;
        makeObservable(this, {
            agentListStore: observable,
           
            id: observable,
            uuid: observable,
            name: observable,
            description: observable,
            type: observable,
            category: observable,
            
            tenantId: observable,
            instructionUuid: observable,
            metadata: observable,
            createdAt: observable,
            owner: observable,
            createdBy: observable,
            updatedAt: observable,
            status: observable,
            version: observable,
            scope: observable,
            tags: observable,
            vaultUuid: observable,
            starters: observable,
            documents: observable,
            drives: observable,
            documentsLoading: observable,
            drivesLoading: observable,
            assignDocumentLoading: observable,
            assignVaultLoading: observable,
            isLoading: observable,
            error: observable,
            isSaving: observable,
            isCreating: observable,
            agentCreated: observable,
            errorCreatingAgent: observable,
            showSavingAlert: observable,
            lastLoadedAgentId: observable,
            lastLoadedVaultId: observable,
            

            setName: action,
            setShowSavingAlert: action,
            updateField: action,
            saveAgent: action,
            setDescription: action,
            setType: action,
            setCategory: action,
            setTags: action,
            addStarter: action,
            removeStarter: action,
            setError: action,
            fetchAgent: action,
            updateFromGetResponse: action,
            updateFromUpdateResponse: action,
            setDocuments: action,
            setDrives: action,
            setAgentCreated: action,
            createAgent: action,
            fetchDocumentsDrivesData: flow,
        });
    }

    setName = (name) => { 
        this.name = name;
        this.debounceSave();
    };
    
    setDescription = (description) => { 
        this.description = description;
        this.debounceSave();
    };
    
    setType = (type) => { 
        this.type = type;
        this.saveImmediately();
    };
    
    setCategory = (category) => { 
        this.category = category;
        this.saveImmediately();
    };
    
    setTags = (tags) => { 
        this.tags = tags;
        this.debounceSave();
    };

    addTag = (tag) => {
        const currentTags = this.tags ? this.tags.split(',') : [];
        if (!currentTags.includes(tag)) {
            currentTags.push(tag);
            this.tags = currentTags.join(',');
            this.saveImmediately();
        }
    };

    removeTag = (tagToRemove) => {
        const currentTags = this.tags ? this.tags.split(',') : [];
        this.tags = currentTags.filter(tag => tag !== tagToRemove).join(',');
        this.saveImmediately();
    };

    addStarter = (starter) => {
        this.starters = [...this.starters, starter];
        this.debounceSave();
    };

    removeStarter = (index) => {
        this.starters = this.starters.filter((_, i) => i !== index);
        this.debounceSave();
    };

    setDocuments = (documents) => {
        this.documents = documents;
    };

    setDrives = (drives) => {
        this.drives = drives;
    };

    setAgentCreated(created) {
        this.agentCreated = created;
    }

    setError = (error) => { this.error = error; };

    setShowSavingAlert = (show) => {
        this.showSavingAlert = show;
        if (show) {
            // Mantener el Alert visible por 3 segundos después de que se complete el guardado
            setTimeout(() => {
                this.showSavingAlert = false;
            }, 3000);
        }
    };

    updateField = (fieldName, value) => {
        this[fieldName] = value;
        this.debounceSave();
    };

    saveImmediately = async () => {
        if (this.lastSaveTimeout) {
            clearTimeout(this.lastSaveTimeout);
            this.lastSaveTimeout = null;
        }
        return this.saveAgent();
    };

    debounceSave = () => {
        if (this.lastSaveTimeout) {
            clearTimeout(this.lastSaveTimeout);
        }
        
        this.lastSaveTimeout = setTimeout(() => {            
            this.saveAgent();            
        }, 1000); 
    };

    saveAgent = async () => {
       
        if (!this.uuid || this.isSaving) return;
        
        const currentRequestId = ++this.lastSaveRequestId;
        
        const snapshot = {
            requestId: currentRequestId,
            name: this.name,
            description: this.description,
            type: this.type,
            category: this.category,
            tags: this.tags,
            metadata: this.metadata ? { ...this.metadata } : {},
            starters: [...this.starters]
        };
        
        this.lastSaveSnapshot = snapshot;

        try {    
            this.isSaving = true;
            this.setShowSavingAlert(true);
            
            const body = {
                name: this.name,
                description: this.description,
                tenant_id: this.tenantId,
                metadata: this.metadata || {},
                owner: this.owner,
                created_by: this.createdBy,
                status: this.status,
                type: this.type,
                version: this.version,
                scope: this.scope,
                tags: this.tags,
                category: this.category,
                starters: this.starters,
                image: this.metadata?.imageUrl || '',
                signature: ''
            };

            const { data } = await updateAgent(this.uuid, body);
           
            const isMostRecentRequest = currentRequestId === this.lastSaveRequestId;
            const hasNoChanges = snapshot && 
                this.name === snapshot.name &&
                this.description === snapshot.description &&
                this.type === snapshot.type &&
                this.category === snapshot.category &&
                this.tags === snapshot.tags &&
                JSON.stringify(this.metadata) === JSON.stringify(snapshot.metadata) &&
                JSON.stringify(this.starters) === JSON.stringify(snapshot.starters);
            
            if (isMostRecentRequest && hasNoChanges) {
                this.updateFromUpdateResponse(data);
            }
            
        } catch (error) {
            this.setError('Error saving agent');
            console.error('Error in saveAgent:', error);
        } finally {
            this.isSaving = false;
        }
    };

    updateFromGetResponse = (data) => {
        this.id = data.id;
        this.uuid = data.uuid;
        this.name = data.name;
        this.description = data.description;
        this.instructionUuid = data.instructionUuid || data.instruction_uuid || "";
        this.type = data.type;
        this.category = data.category;
        this.tenantId = data.tenantId;
        this.metadata = data.metadata || {};
        this.createdAt = data.createdAt;
        this.owner = data.owner;
        this.createdBy = data.createdBy;
        this.updatedAt = data.updatedAt;
        this.status = data.status;
        this.version = data.version;
        this.scope = data.scope;
        this.tags = data.tags;
        this.msgCode = data.msgCode;
        this.vaultUuid = data.vaultUuid;
        
        // Prioridad: starters > conversation_starters > metadata.conversation_starters
        // Verificamos que el array tenga elementos, no solo que exista
        if (Array.isArray(data.starters) && data.starters.length > 0) {
            this.starters = data.starters;
        } else if (Array.isArray(data.conversation_starters) && data.conversation_starters.length > 0) {
            this.starters = data.conversation_starters;
        } else if (Array.isArray(data.metadata?.conversation_starters) && data.metadata.conversation_starters.length > 0) {
            this.starters = data.metadata.conversation_starters;
        } else {
            this.starters = [];
        }
    };

    updateFromUpdateResponse = (data) => {
        this.id = data.id;
        this.uuid = data.uuid;
        this.name = data.name;
        this.description = data.description;
        this.type = data.type;
        this.category = data.category;
        this.tenantId = data.tenant_id;
        this.metadata = data.metadata || {};
        this.createdAt = data.created_at;
        this.owner = data.owner;
        this.createdBy = data.created_by;
        this.updatedAt = data.updated_at;
        this.status = data.status;
        this.version = data.version;
        this.scope = data.scope;
        this.tags = data.tags;

        // Actualizar los starters si existen en la respuesta (prioridad: starters > conversation_starters > metadata)
        // Verificamos que el array tenga elementos, no solo que exista
        if (Array.isArray(data.starters) && data.starters.length > 0) {
            this.starters = data.starters;
        } else if (Array.isArray(data.conversation_starters) && data.conversation_starters.length > 0) {
            this.starters = data.conversation_starters;
        } else if (Array.isArray(data.metadata?.conversation_starters) && data.metadata.conversation_starters.length > 0) {
            this.starters = data.metadata.conversation_starters;
        }
    };

    fetchAgent = async (uuid) => {
        try {
            this.isLoading = true;
            this.error = null;
            
            const { data } = await getAgentById(uuid);
            this.updateFromGetResponse(data);
            
        } catch (error) {
            this.error = "Error fetching agent details";
            throw error;
        } finally {
            this.isLoading = false;
        }
    };

    fetchDrives = async (uuid, whitLoading = true) => {       
        this.drivesLoading = whitLoading;
        try {
            const { data } = await getDrivesByAgent(uuid);
            this.setDrives(data.data || []);
        } catch (error) {
            console.error('Error fetching drives:', error);
            this.setDrives([]);
        } finally {
            this.drivesLoading = false;
        }
    };

    fetchDocuments = async (uuid, whitLoading = true) => {       
        this.documentsLoading = whitLoading;
        try {
            const { data } = await getDocumentsByAgent(uuid);
            this.setDocuments(data.data || []);
        } catch (error) {
            console.error('Error fetching documents:', error);
            this.setDocuments([]);
        } finally {
            this.documentsLoading = false;
        }
    };

    *fetchDocumentsDrivesData(uuid){
        try {
            yield Promise.all([
                this.fetchDocuments(uuid),
                this.fetchDrives(uuid)
            ]);
            this.lastLoadedAgentId = uuid;
        } catch (error) {
            console.error('Error fetching agent data:', error);
            throw error;
        }
    };

    assignDocumentAgent = async (body) => {
        this.assignDocumentLoading = true;
        try {
            const response = await assignAgentToDocument(body);
            return response;
        } catch (error) {
            console.error('Error assigning document to agent:', error);
            throw error;
        } finally {
            this.assignDocumentLoading = false;
        }
    };

    assignVaultAgent = async (body) => {
        this.assignVaultLoading = true;
        try {
            // Adaptar formato antiguo al nuevo formato
            const vaultUuid = body.vaultUuid || body.vault_uuid;
            const agentUuid = body.agentUuid || body.agent_uuid;
            const privilege = body.privilege || 'READER';
            const description = body.description || 'Agent assigned to vault';
            
            const response = await assignAgentToVault(vaultUuid, agentUuid, privilege, description);
            return response;
        } catch (error) {
            console.error('Error assigning vault to agent:', error);
            throw error;
        } finally {
            this.assignVaultLoading = false;
        }
    };

    createAgent = async (agentData) => {
        try {
            this.isCreating = true;
            this.error = null;
            
            const body = {
                name: agentData.name,
                description: agentData.description || '',
                starters: agentData.starters || [],
                metadata: agentData.metadata || {},
                owner: agentData.owner,
                createdBy: agentData.createdBy,
                status: agentData.status || 'active',
                type: agentData.type || 'ai',
                version: agentData.version || 1,
                scope: agentData.scope || 'internal',
                tags: agentData.tags || '',
                category: agentData.category || 'Service',
            };

            const { data } = await createAgent(body);
            this.setAgentCreated(true);
            this.updateFromGetResponse(data.body);

        } catch (error) {
            this.errorCreatingAgent = true;
            this.setError('Error creating agent');
            this.setAgentCreated(false);
            throw error;
        } finally {
            this.isCreating = false;
            this.errorCreatingAgent = false;
        }
    }
     unassignAgentFromDocument = async (uuid, agent_id) => {
        this.unassignAgentFromDocumentLoading = true;
        try {
            const response = await unassignAgentFromDocument(uuid, agent_id);
            this.fetchDocuments(agent_id, false);
            return response;
        } catch (error) {
            console.error('Error unassigning agent from document:', error);
            throw error;
        } finally {
            this.unassignAgentFromDocumentLoading = false;
        }
        
    };
    unassignAgentFromMultipleDocuments = async (uuids, agent_id) => {
        this.unassignAgentFromDocumentLoading = true;
        try {
            const unassignPromises = uuids.map(uuid => 
                unassignAgentFromDocument(uuid, agent_id)
                    .catch(error => {
                        console.error(`Error unassigning document ${uuid}:`, error);
                        return { error, uuid }; 
                    })
            );            
            const results = await Promise.all(unassignPromises);            
            const errors = results.filter(result => result.error);
            if (errors.length > 0) {
                console.error(`Failed to unassign ${errors.length} documents:`, 
                    errors.map(e => e.uuid).join(', '));
            }            
            await this.fetchDocuments(agent_id, false);
            return {
                success: results.length - errors.length,
                failed: errors.length,
                errors: errors
            };
        } catch (error) {
            console.error('Error in batch unassign operation:', error);
            throw error;
        } finally {
            this.unassignAgentFromDocumentLoading = false;
        }
    };

    unassignAgentFromMultipleDrives = async (driveUuids, agent_id) => {
        this.unassignAgentFromDriveLoading = true;
        try {
            console.log('Unassigning drives:', driveUuids);
            await this.fetchDrives(agent_id, false);
            return {
                success: driveUuids.length,
                failed: 0,
                errors: []
            };
        } catch (error) {
            console.error('Error in batch unassign drives operation:', error);
            throw error;
        } finally {
            this.unassignAgentFromDriveLoading = false;
        }
    };
}

export default AgentStore;